import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, X, Clock } from "lucide-react";
import { CollectionStrip, CollectionBanner } from "./CollectionStrip";
import { ComboStrip } from "./ComboStrip";
import { ComboPreview } from "./ComboPreview";
import { trackSearch, trackView } from "@/lib/track";
import { api } from "@/lib/api";
import { CATEGORIES, formatINR } from "@/config";
import { newLaunchItems } from "@/lib/newLaunch";

const PAGE_SIZE = 24;
const RECENT_KEY = "vs_recent_searches";
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
import ProductCard from "./ProductCard";
import ProductDialog from "./ProductDialog";

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All Products");
  const [query, setQuery] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [sort, setSort] = useState("popular");
  const [selected, setSelected] = useState(null);
  const [failed, setFailed] = useState(false);
  const [categories, setCategories] = useState(CATEGORIES);
  const [allCategories, setAllCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [offers, setOffers] = useState([]);
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [collection, setCollection] = useState(null);
  const [popular, setPopular] = useState(new Map());
  const [recent, setRecent] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(saved) ? saved.filter((item) => typeof item === "string").slice(0, 6) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(recent)); } catch { /* Search stays usable with blocked/full browser storage. */ }
  }, [recent]);

  useEffect(() => {
    const openCombo = (event) => {
      if (!event.detail?.offerId) return;
      setSelected(null);
      setSelectedCombo(event.detail.offerId);
    };
    window.addEventListener("vs:combo", openCombo);
    return () => window.removeEventListener("vs:combo", openCombo);
  }, []);

  const remember = useCallback((term) => {
    const clean = term.trim();
    if (clean.length < 2) return;
    setRecent((prev) => {
      const kept = prev.filter((t) => {
        const low = t.toLowerCase();
        return low !== clean.toLowerCase() && !clean.toLowerCase().startsWith(low);
      });
      const next = [clean, ...kept].slice(0, 6);
      return next;
    });
  }, []);

  const clearRecent = () => {
    try { localStorage.removeItem(RECENT_KEY); } catch { /* Clear the visible list even when storage is blocked. */ }
    setRecent([]);
  };

  const fetchProducts = useCallback(() => {
    setLoading(true);
    setFailed(false);
    api.get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    if (!products.length) return;
    const wanted = new URLSearchParams(window.location.search).get("p");
    if (!wanted) return;
    const key = wanted.toLowerCase();
    const match = products.find((p) => p.id === wanted || (p.sku || "").toLowerCase() === key);
    if (!match) return;
    setSelected(match);
    document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
  }, [products]);

  useEffect(() => {
    const applyHash = () => {
      const hash = slug(window.location.hash.replace("#", ""));
      if (!hash) return;
      const match = hash === "newlaunch" ? "New Launch" : allCategories.find((c) => slug(c) === hash);
      if (!match) return;
      setCategory(match);
      document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, [allCategories]);

  useEffect(() => {
    api.get("/collections")
      .then((res) => setCollections(res.data))
      .catch(() => {});
    api.get("/offers")
      .then((res) => setOffers(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selected) trackView(selected);
  }, [selected]);

  useEffect(() => {
    const onNav = (e) => {
      const { label = "", href = "" } = e.detail || {};
      const keys = [slug(href.replace("#", "")), slug(label)].filter(Boolean);
      const col = collections.find((c) => keys.includes(slug(c.slug)) || keys.includes(slug(c.name)));
      if (col) {
        setCollection(col);
        setQuery("");
        setCategory("All Products");
        document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
        return;
      }
      const cat = allCategories.find((c) => keys.includes(slug(c)));
      setCollection(null);
      if (cat) {
        setCategory(cat);
        setQuery("");
      } else {
        setCategory("All Products");
        setQuery(label);
      }
      document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("vs:nav", onNav);
    return () => window.removeEventListener("vs:nav", onNav);
  }, [allCategories, collections]);

  useEffect(() => {
    api.get("/categories")
      .then((res) => {
        setAllCategories(res.data.map((c) => c.name));
        setCategories(["All Products", ...res.data.filter((c) => c.product_count > 0).map((c) => c.name)]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/popular")
      .then(({ data }) => setPopular(new Map((data.products || []).map((p) => [p.product_id, p.score]))))
      .catch(() => {});
  }, []);

  const newLaunch = useMemo(() => newLaunchItems(products), [products]);
  const newLaunchCount = newLaunch.items.length;
  const tabs = useMemo(() => {
    const base = newLaunchCount ? [categories[0], "New Launch", ...categories.slice(1)] : categories;
    return base.includes(category) ? base : [...base, category];
  }, [categories, newLaunchCount, category]);

  const [shown, setShown] = useState(PAGE_SIZE);

  const visible = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let list = products;
    if (collection) {
      const ids = new Set(collection.product_ids || []);
      list = list.filter((p) => ids.has(p.id));
    }
    if (!terms.length) {
      if (collection) {
        // collection view is not category filtered
      } else if (category === "New Launch") list = newLaunch.items;
      else if (category !== "All Products") list = list.filter((p) => p.category === category);
    } else {
      list = list.filter((p) => {
        const haystack = [p.name, p.sku, p.category, p.description, ...(p.substrate || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((t) => haystack.includes(t));
      });
    }
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "popular" && popular.size) {
      list = [...list].sort((a, b) => (popular.get(b.id) || 0) - (popular.get(a.id) || 0));
    }
    return list;
  }, [products, newLaunch, category, query, sort, collection, popular]);

  useEffect(() => { setShown(PAGE_SIZE); }, [category, query, sort, collection]);

  const suggestions = useMemo(
    () => (query.trim().length < 2 ? [] : visible.filter((p) => p.is_live !== false).slice(0, 6)),
    [query, visible]
  );

  useEffect(() => {
    if (query.trim().length < 2) return;
    const t = setTimeout(() => {
      if (visible.length) remember(query);
      trackSearch(query);
    }, 2000);
    return () => clearTimeout(t);
  }, [query, visible.length, remember]);

  useEffect(() => {
    const close = (e) => { if (!e.target.closest?.("[data-search-box]")) setSuggestOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <section id="catalog" data-testid="catalog-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
      {selectedCombo && <ComboPreview key={selectedCombo} offerId={selectedCombo} onClose={() => setSelectedCombo(null)} />}
      <div className="mb-10 flex flex-col gap-2">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400">Our Products</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
          Full Catalogue <span className="text-outline">/ Press-Ready</span>
        </h2>
      </div>

      {collection ? (
        <CollectionBanner collection={collection} count={visible.length} onClear={() => setCollection(null)} />
      ) : (
        <>
          <CollectionStrip collections={collections} onPick={setCollection} />
          <ComboStrip offers={offers} products={products} onPreview={(combo) => setSelectedCombo(combo.id)} />
        </>
      )}

      <div className="sticky top-[60px] z-30 sm:top-[73px] -mx-4 mb-8 flex flex-col gap-4 border-b border-white/5 bg-[#0A0B0E]/90 px-4 py-4 backdrop-blur-xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:flex-row lg:items-center lg:justify-end lg:px-8" data-testid="catalog-toolbar">
        <div className="flex flex-wrap gap-2 lg:flex-nowrap lg:gap-3 lg:shrink-0">
          <div className="relative basis-full lg:basis-auto" data-search-box>
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="catalog-search-input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSuggestOpen(true); }}
              onFocus={() => setSuggestOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setSuggestOpen(false);
                if (e.key === "Enter") { remember(query); setSuggestOpen(false); }
              }}
              placeholder="Search blanks..."
              className="w-full lg:w-56 rounded-full border border-white/10 bg-[#12141A] py-2.5 pl-10 pr-9 text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none"
            />
            {query && (
              <button
                data-testid="catalog-search-clear-btn"
                aria-label="Clear search"
                onClick={() => { setQuery(""); setSuggestOpen(false); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-cyan-300"
              >
                <X size={14} />
              </button>
            )}
            {suggestOpen && !query.trim() && recent.length > 0 && (
              <div
                data-testid="catalog-recent-searches"
                className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-white/10 bg-[#12141A] p-3 shadow-2xl shadow-black/60 lg:w-80"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Recent searches</span>
                  <button
                    data-testid="clear-recent-searches-btn"
                    onClick={clearRecent}
                    className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 transition-colors hover:text-red-400"
                  >
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recent.map((t) => (
                    <button
                      key={t}
                      data-testid={`recent-search-${slug(t)}`}
                      onClick={() => { setQuery(t); setSuggestOpen(true); }}
                      className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-300"
                    >
                      <Clock size={11} className="text-slate-500" /> {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {suggestOpen && suggestions.length > 0 && (
              <div
                data-testid="catalog-search-suggestions"
                className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-white/10 bg-[#12141A] shadow-2xl shadow-black/60 lg:w-80"
              >
                {suggestions.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`search-suggestion-${p.id}`}
                    onClick={() => { remember(query); setSelected(p); setSuggestOpen(false); }}
                    className="flex w-full items-center gap-3 border-b border-white/5 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-white/[0.05]"
                  >
                    <img
                      src={p.image || "/placeholder-product.svg"}
                      alt=""
                      onError={(e) => { e.currentTarget.src = "/placeholder-product.svg"; }}
                      className="h-10 w-10 shrink-0 rounded-lg object-cover"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-slate-200">{p.name}</span>
                      <span className="block truncate font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        {p.sku ? `${p.sku} · ` : ""}{p.category}
                      </span>
                    </span>
                    <span className="shrink-0 font-display text-sm font-bold text-cyan-300">{formatINR(p.price)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <select
            data-testid="catalog-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="min-w-0 flex-1 rounded-full border border-white/10 bg-[#12141A] px-4 py-2.5 text-sm text-slate-300 focus:border-fuchsia-400/50 focus:outline-none lg:flex-none"
          >
            <option value="popular">Most Searched</option>
            <option value="featured">Product Code</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
          <select
            data-testid="category-filter-select"
            aria-label="Filter by category"
            value={collection ? "All Products" : (tabs.includes(category) ? category : "All Products")}
            onChange={(e) => { setCollection(null); setCategory(e.target.value); }}
            className="min-w-0 flex-1 rounded-full border border-white/10 bg-[#12141A] px-4 py-2.5 text-sm text-slate-300 focus:border-cyan-400/50 focus:outline-none lg:flex-none lg:w-56"
          >
            {tabs.map((c) => (
              <option key={c} value={c}>
                {c === "New Launch" ? `★ New Launch (${newLaunchCount})` : c}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-72 animate-pulse rounded-2xl bg-[#181B24] sm:h-96" />
          ))}
        </div>
      ) : failed ? (
        <div data-testid="catalog-error-state" className="rounded-2xl border border-red-400/20 bg-[#12141A] p-16 text-center">
          <p className="text-sm text-slate-400">We couldn't load the catalogue just now.</p>
          <button
            data-testid="catalog-retry-btn"
            onClick={fetchProducts}
            className="mt-5 rounded-full border border-cyan-400/40 px-6 py-2.5 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
          >
            Try again
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="catalog-empty-state" className="rounded-2xl border border-white/10 bg-[#12141A] p-16 text-center text-slate-500">
          No blanks match your search.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4" data-testid="product-grid">
          {visible.slice(0, shown).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onOpen={setSelected} />
          ))}
        </div>
      )}

      {!loading && !failed && visible.length > 0 && (
        <div className="mt-8 flex flex-col items-center gap-3">
          <p data-testid="catalog-result-count" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Showing {Math.min(shown, visible.length)} of {visible.length} blanks
          </p>
          {shown < visible.length && (
            <button
              data-testid="catalog-load-more-btn"
              onClick={() => setShown((n) => n + PAGE_SIZE)}
              className="h-12 rounded-full border border-cyan-400/40 px-8 text-sm font-semibold text-cyan-300 transition-colors active:bg-cyan-400/10"
            >
              Load more blanks
            </button>
          )}
        </div>
      )}

      <ProductDialog product={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
